import React, { useMemo, lazy, Suspense, useCallback, useRef, useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { logSearchResultSelectedEvent } from '@alltrails/shared/utils/amplitudeHelpers';
import { debounce } from 'debounce';
import { contextPropTypes } from '../../../proptypes/shared';
import * as styles from './styles/styles.module.scss';
import SearchBar from '../SearchBar';
import useSearch from '../../../hooks/useSearch';
import { logSearchBoxClicked } from '../../../utils/explore_analytics_helpers';
import { HOMEPAGE } from '../../../utils/constants/SearchAnalyticsConstants';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';
import { ARROW_UP, ARROW_DOWN, ESC } from '../../../utils/constants/key_codes';
import { SearchInsightsContext } from '../../SearchInsightsProvider';

const MobileSearchBar = lazy(() => import('../MobileSearchBar'));

const HomeSearchBar = ({ latLng, windowWidth, context }) => {
  const searchContainer = useRef();

  const { currentUser, primaryAlgoliaIndexId } = context;

  const analyticsTagsParams = {
    user: currentUser,
    locale: context.languageRegionCode,
    origin: HOMEPAGE,
    mobile: context.mobileBrowser
  };

  const { updateSearchId, logClickEvent } = useContext(SearchInsightsContext);

  const { searchState, updateQuery, updateActiveTabFilter, toggleEngaged, updateActiveIndexResult } = useSearch({
    primaryAlgoliaIndexId,
    latLng,
    languageRegionCode: context.languageRegionCode,
    analyticsTagsParams
  });

  // Whenever we update the search id inform our global tracking context about it.
  useEffect(() => {
    if (searchState.queryID) {
      updateSearchId(searchState.queryID);
    }
  }, [updateSearchId, searchState.queryID]);

  const navigateToSearchResult = result => {
    window.location = result.url;
  };

  const handleSearchResultClick = useCallback(
    (result, idx) => {
      logSearchResultSelectedEvent(searchState.query, idx + 1, result, HOMEPAGE);
      logClickEvent(result.objectID, idx + 1);
      navigateToSearchResult(result);
    },
    [searchState.query, logClickEvent]
  );

  const handleSearchBoxFocus = useCallback(() => {
    logSearchBoxClicked(HOMEPAGE);
  }, []);

  const isMobileWidth = useMemo(() => {
    return windowWidth && windowWidth < DESKTOP_BREAKPOINT;
  }, [windowWidth]);

  const mobileSearchBar = useMemo(() => {
    return isMobileWidth && searchState.engaged;
  }, [isMobileWidth, searchState.engaged]);

  const checkForFocusOut = debounce(() => {
    if (searchContainer.current && !searchContainer.current.contains(document.activeElement)) {
      toggleEngaged(false);
    }
  }, 800);

  const handleKeyUp = e => {
    // this handles keyboard interactions, for arrow_up and down as well as esc
    if (window.keyboardUser) {
      checkForFocusOut();
    }

    if ([ARROW_UP, ARROW_DOWN].includes(e.keyCode)) {
      e.preventDefault();
    }

    switch (e.keyCode) {
      case ESC:
        toggleEngaged(false);
        break;
      case ARROW_UP:
        return searchState.activeIndexResult === 0
          ? updateActiveIndexResult(searchState.results.length - 1)
          : updateActiveIndexResult(searchState.activeIndexResult - 1);
      case ARROW_DOWN:
        return searchState.activeIndexResult + 1 === searchState.results.length
          ? updateActiveIndexResult(0)
          : updateActiveIndexResult(searchState.activeIndexResult + 1);
      default:
        return null;
    }
  };

  return (
    <>
      {mobileSearchBar &&
        ReactDOM.createPortal(
          <Suspense fallback={null}>
            <MobileSearchBar
              query={searchState.query}
              loading={searchState.loading}
              updateQuery={updateQuery}
              results={searchState.results}
              filters={searchState.filters}
              activeFilterIndex={searchState.activeFilterIndex}
              updateActiveTabFilter={updateActiveTabFilter}
              toggleEngaged={toggleEngaged}
              engaged={searchState.engaged}
              context={context}
              activeIndexResult={searchState.activeIndexResult}
              updateActiveIndexResult={updateActiveIndexResult}
              handleSearchResultClick={handleSearchResultClick}
              handleSearchBoxFocus={handleSearchBoxFocus}
            />
          </Suspense>,
          document.getElementById('modalPortal')
        )}
      <div ref={searchContainer} className={styles.container} onKeyUp={handleKeyUp}>
        <SearchBar
          query={searchState.query}
          loading={searchState.loading}
          updateQuery={updateQuery}
          results={searchState.results}
          filters={searchState.filters}
          activeFilterIndex={searchState.activeFilterIndex}
          updateActiveTabFilter={updateActiveTabFilter}
          toggleEngaged={toggleEngaged}
          engaged={searchState.engaged}
          context={context}
          activeIndexResult={searchState.activeIndexResult}
          updateActiveIndexResult={updateActiveIndexResult}
          handleSearchResultClick={handleSearchResultClick}
          mobileSearchBar={mobileSearchBar}
          handleSearchBoxFocus={handleSearchBoxFocus}
        />
      </div>
    </>
  );
};

HomeSearchBar.propTypes = {
  latLng: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  windowWidth: PropTypes.number,
  context: contextPropTypes.isRequired
};

HomeSearchBar.defaultProps = {
  latLng: null,
  windowWidth: null
};

export default HomeSearchBar;
